import ParamGroup from 'mediamonks-webgl/utils/uiParams/ParamGroup';
import DatGui from 'mediamonks-webgl/utils/uiParams/DatGui';
import Renderer from 'mediamonks-webgl/renderer/render/Renderer';
import CanvasManager from 'mediamonks-webgl/renderer/core/CanvasManager';
import WebGLPreLoader from 'mediamonks-webgl/renderer/core/WebGLPreLoader';
import RendererWebGL1 from 'mediamonks-webgl/renderer/render/RendererWebGL1';
import MaterialLoader from 'mediamonks-webgl/renderer/material/MaterialLoader';
import OrbitCamera from 'mediamonks-webgl/utils/camera/OrbitCamera';
import Time from 'mediamonks-webgl/renderer/core/Time';
import Texture2D from "mediamonks-webgl/renderer/texture/Texture2D";
import TextureLoader from "mediamonks-webgl/utils/loaders/TextureLoader";
import TextLoader from "mediamonks-webgl/utils/loaders/TextLoader";

import modelURL from './data/Town.obj';
import ObjMesh from "mediamonks-webgl/utils/mesh/ObjMesh";
import ObjSubMeshParser from "mediamonks-webgl/utils/loaders/ObjSubMeshParser";
import Vector3 from "mediamonks-webgl/renderer/math/Vector3";
import Color from "mediamonks-webgl/renderer/math/Color";
import GL from "mediamonks-webgl/renderer/base/GL";
import Material from "mediamonks-webgl/renderer/material/Material";
import MeshUtils from "mediamonks-webgl/utils/mesh/MeshUtils";
import Mesh from "mediamonks-webgl/renderer/mesh/Mesh";
import Cylinder from "mediamonks-webgl/utils/mesh/Cylinder";

const assets = require.context(
  './data',
  true,
  /(\.png|\.jpg)$/
);


export default class Townscaper {
  private datGui: DatGui | undefined;
  private renderer: Renderer;
  private canvasManager: CanvasManager;

  private materialNoShading: MaterialLoader;
  private materialReflection: MaterialLoader;
  private materialBackFace: MaterialLoader;
  private materialTexAndLighting: MaterialLoader;
  private materialOutline: MaterialLoader;
  private materialFencing: MaterialLoader;
  private materialWater: MaterialLoader;
  private meshes: { [k: string]: ObjMesh } = {};

  private textureColor: Texture2D;
  private textureMaterial: Texture2D;
  private texturePalette: Texture2D;
  private textureClouds: Texture2D;

  private cameraController: OrbitCamera;

  private meshBackground: Cylinder;
  private materialBackground: MaterialLoader;

  constructor(canvasParent: HTMLElement, preloader: WebGLPreLoader, uiVisible: boolean) {
    uiVisible = false;

    this.canvasManager = new CanvasManager(canvasParent);

    this.renderer = new RendererWebGL1(preloader, this.canvasManager.canvas, require.context('./shaders', true), true, false, true, true);

    this.cameraController = new OrbitCamera(this.renderer, 1, 0.01, 1000);
    this.cameraController.lookAt(new Vector3(40, 30, 20), new Vector3(0, 2, 0));

    this.materialNoShading = new MaterialLoader(this.renderer, preloader, 'townscaper');
    this.materialNoShading.addShaderDefines('NO_SHADING');
    this.materialNoShading.setCullingFrontFace();

    this.materialBackFace = new MaterialLoader(this.renderer, preloader, 'townscaper');

    this.materialReflection = new MaterialLoader(this.renderer, preloader, 'townscaper');
    this.materialReflection.addShaderDefines('LIGHTING');
    this.materialReflection.addShaderDefines('REFLECTION');

    this.materialTexAndLighting = new MaterialLoader(this.renderer, preloader, 'townscaper');
    this.materialTexAndLighting.addShaderDefines('LIGHTING');
    this.materialTexAndLighting.setCullingFrontFace();

    this.materialOutline = new MaterialLoader(this.renderer, preloader, 'townscaper');
    this.materialOutline.addShaderDefines('LIGHTING');
    this.materialOutline.addShaderDefines('OUTLINE');

    this.materialWater = new MaterialLoader(this.renderer, preloader, 'townscaper');
    this.materialWater.setAdditiveBlending();
    this.materialWater.addShaderDefines('WATER');
    this.materialWater.setCullingFrontFace();

    this.materialFencing = new MaterialLoader(this.renderer, preloader, 'townscaper');
    this.materialFencing.setCullingDisabled();
    this.materialFencing.setAlphaBlending();
    this.materialFencing.addShaderDefines('FENCING');

    this.meshBackground = new Cylinder(this.renderer, 64, 1, 100 / Math.PI, 100, false);
    this.materialBackground = new MaterialLoader(this.renderer, preloader, 'background');
    this.materialBackground.setCullingFrontFace();
    this.materialBackground.depthWrite = false;

    this.textureColor = new TextureLoader(this.renderer, preloader, assets('./TownColor.png'));
    this.textureColor.filterNearest = false;
    this.textureColor.clampToEdge = false;
    this.textureMaterial = new TextureLoader(this.renderer, preloader, assets('./TownMaterial.png'));
    this.textureMaterial.filterNearest = true;
    this.textureMaterial.clampToEdge = false;
    this.texturePalette = new TextureLoader(this.renderer, preloader, assets('./TownPalette.png'));
    this.texturePalette.filterNearest = true;
    this.texturePalette.clampToEdge = false;
    this.textureClouds = new TextureLoader(this.renderer, preloader, assets('./clouds.jpg'));
    this.textureClouds.useMips = true;
    this.textureClouds.clampToEdge = false;

    const loader = new TextLoader(preloader, modelURL);
    preloader.setCallbackForPreloadable(loader, () => {
      this.parseObj(loader.text);
    });

    if (uiVisible) {
      this.datGui = new DatGui(preloader);
    }
  }

  private initDropZone(dropzone: HTMLCanvasElement) {
    dropzone.ondragover = function (e) {
      e.preventDefault();
      return false;
    };

    dropzone.ondrop = (e) => {
      e.preventDefault();
      // @ts-ignore
      const file = e.dataTransfer.files[0] as File;
      if (file && file.name.split('.').pop() === 'obj') {
        let reader = new FileReader();
        reader.onload = (e: any) => {
          this.parseObj(e.target.result);
        };
        reader.readAsText(file);
      }
    };
  }

  private parseObj(text: string) {
    this.meshes = {};
    const parser = new ObjSubMeshParser();
    const meshes = parser.parse(this.renderer, text);

    meshes.forEach(m => {
      if (!m.mesh.getNormals()) {
        MeshUtils.generateNormals(m.mesh);
      }
      this.addOutlineNormals(m.mesh);
      this.meshes[m.name as string] = m;
    });
  }

  //will be called from page controller
  public init(): void {
    const paramGroup: ParamGroup = new ParamGroup();
    paramGroup.init('controls');

    const pg = paramGroup.addGroup('Shading', this.getMaterials());

    this.renderer.init();

    this.getMaterials().forEach(mat => {
      mat.setTexture('_TextureColor', this.textureColor);
      mat.setTexture('_TextureMaterial', this.textureMaterial);
      mat.setTexture('_TexturePalette', this.texturePalette);
      mat.setTexture('_TextureClouds', this.textureClouds);
    });

    if (this.datGui) {
      this.datGui.init(paramGroup);
    }

    this.renderer.clearColor = Color.fromHex('#257a84');

    this.initDropZone(this.renderer.canvas);

    this.update();
  }

  private getMaterials(): Material[] {
    return [this.materialNoShading, this.materialTexAndLighting, this.materialFencing, this.materialWater, this.materialBackFace, this.materialOutline, this.materialBackground];
  }

  private addOutlineNormals(m: Mesh) {
    const p = m.getPositions() as Float32Array;
    const n = m.getNormals() as Float32Array;
    const o: Vector3[] = [];

    for (let i = 0; i < p.length; i += 3) {
      if (o[(i / 3) | 0]) continue;

      const no = o[(i / 3) | 0] = new Vector3(n[i + 0], n[i + 1], n[i + 2]);

      for (let j = i + 3; j < p.length; j += 3) {
        if (p[i + 0] == p[j + 0] && p[i + 1] == p[j + 1] && p[i + 2] == p[j + 2]) {
          no.x += n[j + 0];
          no.y += n[j + 1];
          no.z += n[j + 2];
          o[(j / 3) | 0] = no;
        }
      }
      no.normalize();
    }

    m.setAttribute('aOutline', 3, MeshUtils.toFloat32Array(o));
  }

  private draw(name: string, mat: Material) {
    mat.setMatrix('_ViewProjection', this.cameraController.viewProjection);
    mat.setFloat('_Time', Time.instance.time);
    mat.setVector3('_CamPos', this.cameraController.worldPosition);
    mat.setVector2('_Resolution', this.renderer.size);
    this.renderer.draw(this.meshes[name].mesh, mat);
  }

  public update(): void {
    window.requestAnimationFrame(() => this.update());

    this.canvasManager.update(Time.instance.deltaTime);
    this.cameraController.update();

    if (this.cameraController.eulerX >= 0) {
      this.cameraController.rotationController.rotationSpeed.x = 0;
      this.cameraController.eulerX = 0;
    }

    const mat = this.materialBackground;

    mat.setMatrix('_View', this.cameraController.viewMatrix);
    mat.setMatrix('_Projection', this.cameraController.projectionMatrix);
    mat.setFloat('_Time', Time.instance.time);
    mat.setVector3('_CamPos', this.cameraController.worldPosition);
    mat.setVector2('_Resolution', this.renderer.size);

    this.renderer.draw(this.meshBackground, mat);

    // House
    // Fencing
    // Props
    // Plants
    // Windows
    // Sand
    // Water
    // Birds

    this.draw('House', this.materialReflection);
    this.draw('Sand', this.materialReflection);

    this.draw('Water', this.materialWater);

    // Carmacks reverse
    const gl = this.renderer.context;
    gl.colorMask(false, false, false, false);

    this.draw('House', this.materialNoShading);
    this.draw('Plants', this.materialNoShading);
    this.draw('Props', this.materialNoShading);

    gl.depthMask(false);
    gl.enable(GL.STENCIL_TEST);
    gl.stencilFunc(gl.ALWAYS, 1, 0xFF);
    gl.stencilOp(gl.KEEP, gl.INCR, gl.KEEP);

    this.draw('Windows', this.materialNoShading);

    gl.stencilOp(gl.KEEP, gl.DECR, gl.KEEP);
    this.draw('Windows', this.materialBackFace);

    gl.depthMask(true);
    this.renderer.clearDepth();
    gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
    gl.stencilFunc(gl.EQUAL, 1, 0xFF);
    gl.colorMask(true, true, true, true);
    this.draw('Windows', this.materialTexAndLighting);

    gl.colorMask(true, true, true, true);
    gl.stencilFunc(gl.NOTEQUAL, 1, 0xFF);
    this.draw('House', this.materialTexAndLighting);
    gl.disable(GL.STENCIL_TEST);

    this.draw('Sand', this.materialTexAndLighting);
    this.draw('Plants', this.materialTexAndLighting);
    this.draw('Props', this.materialTexAndLighting);
    this.draw('Birds', this.materialTexAndLighting);
    this.draw('Fencing', this.materialFencing);


    this.materialOutline.setFloat('_Outline', 1);
    this.draw('House', this.materialOutline);
    this.draw('Props', this.materialOutline);
    this.materialOutline.setFloat('_Outline', 0.5);
    this.draw('Plants', this.materialOutline);
  }
}
